import { useState, useEffect } from "react";
import { useListContext } from "ra-core";
import { ChevronDown, ChevronRight } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";

export interface FilterOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  color?: string;
}

export interface FilterSection {
  id: string;
  label: string;
  icon?: React.ReactNode;
  type: "single" | "multi";
  options: FilterOption[];
  source: string; // filter key
}

interface FilterSidebarProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sections: FilterSection[];
  sortOptions?: FilterOption[];
}

export const FilterSidebar = ({
  open,
  onOpenChange,
  sections,
  sortOptions,
}: FilterSidebarProps) => {
  const { filterValues, setFilters, displayedFilters, sort, setSort } =
    useListContext();
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});

  // Local state for pending filter changes
  const [pendingFilters, setPendingFilters] = useState<Record<string, any>>(
    () => ({ ...filterValues }),
  );

  // Local state for pending sort
  const [pendingSort, setPendingSort] = useState<string>(() =>
    sort ? `${sort.field}:${sort.order}` : "",
  );

  // Reset pending state when opening
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setPendingFilters({ ...filterValues });
      setPendingSort(sort ? `${sort.field}:${sort.order}` : "");
    }
    onOpenChange(isOpen);
  };

  // Count active filters
  const activeFilterCount = Object.entries(filterValues || {}).filter(
    ([key, value]) => key !== "q" && value !== undefined && value !== "",
  ).length;

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const handleSingleSelect = (source: string, value: string) => {
    setPendingFilters((prev) => ({
      ...prev,
      [source]: prev[source] === value ? undefined : value,
    }));
  };

  const handleMultiSelect = (source: string, value: string) => {
    setPendingFilters((prev) => {
      const currentValues = prev[source] ? prev[source].split(",") : [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v: string) => v !== value)
        : [...currentValues, value];
      return {
        ...prev,
        [source]: newValues.length > 0 ? newValues.join(",") : undefined,
      };
    });
  };

  const handleSortSelect = (value: string) => {
    setPendingSort(value);
  };

  const handleApplyFilters = () => {
    // Clean up undefined values
    const cleanFilters = Object.fromEntries(
      Object.entries(pendingFilters).filter(
        ([_, v]) => v !== undefined && v !== "",
      ),
    );
    setFilters(cleanFilters, displayedFilters);

    // Apply sort if changed
    if (pendingSort) {
      const [field, order] = pendingSort.split(":");
      setSort({ field, order: order as "ASC" | "DESC" });
    }

    onOpenChange(false);
  };

  const handleClearFilters = () => {
    // Only preserve search query
    const searchValue = filterValues?.q;
    const newFilters = searchValue ? { q: searchValue } : {};
    setPendingFilters(newFilters);
    setFilters(newFilters, displayedFilters);
    onOpenChange(false);
  };

  const isValueSelected = (source: string, value: string, type: string) => {
    const currentValue = pendingFilters[source];
    if (!currentValue) return false;
    if (type === "multi") {
      return currentValue.split(",").includes(value);
    }
    return currentValue === value;
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="w-80 sm:max-w-md p-0 flex flex-col">
        <SheetHeader className="px-4 pt-4 pb-3 border-b">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-base font-semibold">
              Filters & Sort
            </SheetTitle>
          </div>
          {activeFilterCount > 0 && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Selected filters:</span>
              <Badge variant="secondary" className="text-xs">
                {activeFilterCount}
              </Badge>
            </div>
          )}
          {activeFilterCount === 0 && (
            <p className="text-sm text-muted-foreground">
              Selected filters: None
            </p>
          )}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          {/* Sort Section */}
          {sortOptions && sortOptions.length > 0 && (
            <div className="border-b">
              <button
                onClick={() => toggleSection("sort")}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Sort by</span>
                </div>
                {(expandedSections["sort"] ?? true) ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </button>

              {(expandedSections["sort"] ?? true) && (
                <div className="px-4 pb-3 space-y-2">
                  <RadioGroup
                    value={pendingSort}
                    onValueChange={handleSortSelect}
                  >
                    {sortOptions.map((option) => (
                      <div
                        key={option.value}
                        className="flex items-center space-x-2 py-1"
                      >
                        <RadioGroupItem
                          value={option.value}
                          id={`sort-${option.value}`}
                        />
                        <Label
                          htmlFor={`sort-${option.value}`}
                          className="text-sm font-normal cursor-pointer flex items-center gap-2"
                        >
                          {option.icon}
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              )}
            </div>
          )}

          {/* Filter Sections */}
          {sections.map((section) => (
            <FilterSectionComponent
              key={section.id}
              section={section}
              expanded={expandedSections[section.id] ?? false}
              onToggle={() => toggleSection(section.id)}
              pendingFilters={pendingFilters}
              onSingleSelect={handleSingleSelect}
              onMultiSelect={handleMultiSelect}
              isValueSelected={isValueSelected}
            />
          ))}
        </div>

        <SheetFooter className="p-4 border-t flex-col gap-2">
          <Button onClick={handleApplyFilters} className="w-full">
            Apply filters
          </Button>
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              onClick={handleClearFilters}
              className="w-full text-muted-foreground"
            >
              Clear all
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

interface FilterSectionComponentProps {
  section: FilterSection;
  expanded: boolean;
  onToggle: () => void;
  pendingFilters: Record<string, any>;
  onSingleSelect: (source: string, value: string) => void;
  onMultiSelect: (source: string, value: string) => void;
  isValueSelected: (source: string, value: string, type: string) => boolean;
}

const FilterSectionComponent = ({
  section,
  expanded,
  onToggle,
  pendingFilters,
  onSingleSelect,
  onMultiSelect,
  isValueSelected,
}: FilterSectionComponentProps) => {
  const selectedCount = section.options.filter((opt) =>
    isValueSelected(section.source, opt.value, section.type),
  ).length;

  return (
    <div className="border-b last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {section.icon}
          <span className="text-sm font-medium">{section.label}</span>
          {selectedCount > 0 && (
            <Badge variant="secondary" className="text-xs h-5 px-1.5">
              {selectedCount}
            </Badge>
          )}
        </div>
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-3 space-y-2">
          {section.type === "single" && (
            <RadioGroup
              value={pendingFilters[section.source] || ""}
              onValueChange={(value) => onSingleSelect(section.source, value)}
            >
              {section.options.map((option) => (
                <div
                  key={option.value}
                  className="flex items-center space-x-2 py-1"
                >
                  <RadioGroupItem
                    value={option.value}
                    id={`${section.id}-${option.value}`}
                  />
                  <Label
                    htmlFor={`${section.id}-${option.value}`}
                    className={cn(
                      "text-sm font-normal cursor-pointer flex items-center gap-2",
                      option.color,
                    )}
                  >
                    {option.icon}
                    {option.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          )}

          {section.type === "multi" && (
            <div className="space-y-2">
              {section.options.map((option) => (
                <div
                  key={option.value}
                  className="flex items-center space-x-2 py-1"
                >
                  <Checkbox
                    id={`${section.id}-${option.value}`}
                    checked={isValueSelected(
                      section.source,
                      option.value,
                      "multi",
                    )}
                    onCheckedChange={() =>
                      onMultiSelect(section.source, option.value)
                    }
                  />
                  <Label
                    htmlFor={`${section.id}-${option.value}`}
                    className={cn(
                      "text-sm font-normal cursor-pointer flex items-center gap-2",
                      option.color,
                    )}
                  >
                    {option.icon}
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Filter button to trigger the sidebar
interface FilterButtonProps {
  onClick: () => void;
  activeCount?: number;
}

export const FilterButton = ({
  onClick,
  activeCount = 0,
}: FilterButtonProps) => (
  <Button variant="outline" onClick={onClick} className="gap-2">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
    Filters
    {activeCount > 0 && (
      <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
        {activeCount}
      </Badge>
    )}
  </Button>
);
